import {
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Paper,
  Container,
  Fade,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DiamondIcon from "@mui/icons-material/Diamond";
import SettingsIcon from "@mui/icons-material/Settings";
import { useFormik, Form, FormikProvider } from "formik";
import { gemPackagesServices } from "../../services/AllServices";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import * as Yup from "yup";
import Iconify from "../../components/iconify";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const validationSchema = Yup.object().shape({
  packageName: Yup.string().required("Package Name is required"),
  gemAmount: Yup.number()
    .typeError("Gem Amount must be a number")
    .required("Gem Amount is required")
    .integer("Gem Amount must be a whole number") 
    .positive("Gem Amount must be greater than zero"),
  price: Yup.number()
    .typeError("Price must be a number")
    .required("Price is required")
    .min(0, "Price cannot be negative"),
  status: Yup.number().required("Status is required"),
});

export default function AddGemPackage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      packageName: "",
      gemAmount: "",
      price: "",
      status: 1,
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const payload = id ? { id, ...values } : values;
      
      const apiCall = id
        ? gemPackagesServices.updateGemPackages(payload)
        : gemPackagesServices.addGemPackage(payload);

      apiCall
        .then((res) => {
          toast.success(res?.data?.message || "Success");
          navigate("/gem-packages"); 
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setValues } = formik;

  useEffect(() => {
    if (id) {
      setLoading(true);
      gemPackagesServices
        .getGemPackage({ id }) 
        .then((res) => {
          const data = res?.data?.data;
          setValues({
            packageName: data.packageName || "",
            gemAmount: data.gemAmount || "",
            price: data.price || "",
            status: data.status !== undefined ? data.status : 1,
          });
        })
        .catch((err) => errorHandler(err))
        .finally(() => setLoading(false));
    }
  }, [id, setValues]);

  if (loading) return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack alignItems="center" justifyContent="center" sx={{ mt: 8, minHeight: '400px' }}>
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
        <Typography sx={{ mt: 3, color: 'text.secondary' }} variant="h6">
          Loading package details...
        </Typography>
      </Stack>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Stack spacing={4}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #00a1dd 0%, #42a5f5 50%, #90caf9 100%)',
              }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                  boxShadow: '0 8px 32px rgba(0, 161, 221, 0.4)'
                }}
              >
                <DiamondIcon sx={{ fontSize: 32, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                  {id ? "Edit" : "Create"} Gem Package
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {id ? "Update the gem amount and pricing for this package" : "Configure a new gem package for the store"}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={4}>
                
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(145deg, #1a202c 0%, #2d3748 100%)',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      background: 'linear-gradient(90deg, rgba(0, 161, 221, 0.1) 0%, rgba(66, 165, 245, 0.1) 100%)',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)'
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: 20, color: 'white' }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        Package Configuration
                      </Typography>
                    </Stack>
                  </Box>

                  <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={6}>
                        <TextField
                          fullWidth
                          label="Package Name"
                          {...getFieldProps("packageName")}
                          error={Boolean(touched.packageName && errors.packageName)}
                          helperText={touched.packageName && errors.packageName}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={6}>
                        <TextField
                          fullWidth
                          label="Gem Amount"
                          type="number"
                          {...getFieldProps("gemAmount")}
                          error={Boolean(touched.gemAmount && errors.gemAmount)}
                          helperText={touched.gemAmount && errors.gemAmount}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={6}>
                        <TextField
                          fullWidth
                          label="Price"
                          type="number"
                          {...getFieldProps("price")}
                          error={Boolean(touched.price && errors.price)}
                          helperText={touched.price && errors.price}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="Status"
                          {...getFieldProps("status")}
                          error={Boolean(touched.status && errors.status)}
                          helperText={touched.status && errors.status}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                              },
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    bottom: 16,
                    zIndex: 99
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Button
                      size="large"
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/gem-packages")}
                      sx={{
                        borderRadius: 2,
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                        }
                      }}
                    >
                      Back to List
                    </Button>
                    
                    <Stack direction="row" spacing={2}>
                      <Button
                        size="large"
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="mdi:content-save" />}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #00a1dd 0%, #42a5f5 100%)',
                          boxShadow: '0 8px 32px rgba(0, 161, 221, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #0091c8 0%, #3b8fd9 100%)',
                            boxShadow: '0 12px 40px rgba(0, 161, 221, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:disabled': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'text.disabled',
                          }
                        }}
                      >
                        {isSubmitting ? "Saving..." : (id ? "Update Package" : "Create Package")}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Form>
          </FormikProvider>
        </Stack>
      </Fade>
    </Container>
  );
}